<script setup lang="ts">
import { parse, stringify } from 'qs';
import DurationFilter from './filters/DurationFilter.vue';
import ButtonGroupFilter from './filters/ButtonGroupFilter.vue';
import CheckboxFilter from './filters/CheckboxFilter.vue';
import PriceFilter from './filters/PriceFilter.vue';
import RadioFilter from './filters/RadioFilter.vue';
import TimeFilter from './filters/TimeFilter.vue';
import StarFilter from './filters/StarFilter.vue';
import FilterPart from './filters/FilterPart.vue';
import InputFilter from './filters/InputFilter.vue';
import SidebarBox from './SidebarBox.vue';
import {
    type Filter,
    type FilterComponents,
    type PriceFilter as PF,
} from '@itinerary/ItineraryModuleTypes';

const { autoApply } = defineProps<{
    autoApply?: boolean;
}>();
const emit = defineEmits<{
    toggle: [];
}>();
const itineraryStore = useItineraryStore();
const gtm = useGtm();
const router = useRouter();
const route = useRoute();
const isMounted = useMounted();
const filterValues = ref<Record<string, any>>({});
const currentModule = useCurrentItineraryModule();
const filters = ref<Filter[]>([]);
const filtersComponents: Record<FilterComponents, any> = {
    DurationFilter,
    ButtonGroupFilter,
    CheckboxFilter,
    PriceFilter,
    RadioFilter,
    TimeFilter,
    StarFilter,
    InputFilter,
};
const showFilters = computed(() => !itineraryStore.noResults);
const loading = computed(() => itineraryStore.filtersLoading);
const showing = computed(() => itineraryStore.packagesLength);
const total = computed(() => itineraryStore.unfilteredCount);
const allFilters = computed(() => itineraryStore.filters);
const filterPath = computed(() => {
    const filterNames = allFilters.value.map(filter => filter.name);
    const query = parse(route.fullPath.split('#')[0].split('?')[1], {
        allowDots: true,
    });
    return Object.entries(query)
        .filter(([key]) => filterNames.includes(key))
        .reduce(
            (previousValue, [currKey, currValue]) => ({
                ...previousValue,
                [currKey]: currValue,
            }),
            {} as Record<string, any>
        );
});
const filtersClear = computed(() => {
    const filtersKeys = Object.keys(filterPath.value);
    return (
        filtersKeys.length === 0 ||
        (filtersKeys.length === 1 && filtersKeys.includes('sortBy'))
    );
});
const applyFilter = (value: any, name: string) => {
    gtm?.trackEvent({
        event: 'srp-filter-change',
        value: name,
    });
    filterValues.value = {
        ...filterValues.value,
        [name]: value,
    };
    if (autoApply) {
        apply();
    }
};
const clear = () => {
    filters.value.forEach(e => {
        filterValues.value[e.name] = null;
    });
    if (autoApply) {
        apply();
    }
};
const apply = () => {
    const prevCounts = JSON.parse(
        localStorage.getItem('filters') || '{}'
    ) as Record<string, number>;
    const newCounts = Object.entries(filterValues.value).reduce(
        (previousValue, [currKey, currVal]) => {
            if (currKey.startsWith('_')) {
                return previousValue;
            }
            if (!currVal || (Array.isArray(currVal) && !currVal?.length)) {
                return {
                    ...previousValue,
                    [currKey]: prevCounts[currKey] || 0,
                };
            } else {
                return {
                    ...previousValue,
                    [currKey]: (prevCounts[currKey] || 0) + 1,
                };
            }
        },
        {} as Record<string, number>
    );
    localStorage.setItem('filters', JSON.stringify(newCounts));
    currentModule.value.setFilterValues(filterValues.value);
    const prevParams = parse(route.fullPath.split('#')[0].split('?')[1], {
        allowDots: true,
    });
    const filterValuesNormalized = stringify(
        { ...prevParams, ...filterValues.value },
        {
            addQueryPrefix: true,
            arrayFormat: 'brackets',
            allowDots: true,
            skipNulls: true,
        }
    );
    const newPath = `${route.path}${filterValuesNormalized}`;
    if (route.fullPath.split('#')[0] !== newPath || route.hash) {
        router.replace(newPath);
    }
    window?.scrollTo({
        top: 0,
        behavior: 'smooth',
    });
};
const refresh = () => {
    if (!itineraryStore.filterValues) {
        return;
    }
    filters.value.forEach(e => {
        filterValues.value[e.name] =
            itineraryStore.filterValues![e.name] || null;
    });
    if (autoApply) {
        apply();
    }
};
watch(
    loading,
    value => {
        if (value || !allFilters.value.length) {
            return;
        }
        const [, prevParams] = route.fullPath.split('?');
        const prevParamsNormalized = parse(prevParams, {
            allowDots: true,
        });
        filters.value = itineraryStore.filters.filter(filter => {
            let is = true;
            if (filter.component === 'PriceFilter') {
                is = !!(filter as PF).min || !!(filter as PF).max;
            }
            return is && (!filter.items || !!filter.items.length);
        });
        const newFilters = filters.value.reduce(
            (prev, curr) => ({
                ...prev,
                [curr.name]: prevParamsNormalized[curr.name] || null,
            }),
            {}
        );
        currentModule.value.setFilterValues(newFilters);
        filterValues.value = { ...newFilters };
    },
    { immediate: true }
);
watch(
    filtersClear,
    (newV, oldV) => {
        if (oldV !== newV && newV) {
            currentModule.value.setFilterValues({});
        }
    },
    { immediate: true }
);
onMounted(() => {
    filterValues.value = itineraryStore.filterValues || {};
});

defineExpose({
    refresh,
    apply,
});
const i18n = useI18n();
</script>

<template>
    <SidebarBox
        v-if="showFilters"
        :title="i18n.t('Filters.filters')"
        class="root-container"
    >
        <template v-if="!loading && isMounted">
            <div class="pre-header">
                <div class="content">
                    <p>
                        {{ i18n.t('Filters.results', { showing, total }) }}
                    </p>
                    <mr-button
                        v-show="!filtersClear"
                        variant="transparent"
                        size="small"
                        @click="clear"
                    >
                        {{ i18n.t('Filters.deleteFilters') }}
                    </mr-button>
                </div>
            </div>
            <template v-for="(filter, index) in filters" :key="filter.name">
                <FilterPart
                    :title="filter.title"
                    initial-state="closed"
                    @toggle="() => emit('toggle')"
                >
                    <component
                        :is="filtersComponents[filter.component]"
                        :model-value="filterValues[filter.name]"
                        v-bind="filter"
                        @update:model-value="
                            (value: any) => applyFilter(value, filter.name)
                        "
                    />
                </FilterPart>
                <div v-if="index !== filters.length - 1" class="divider" />
            </template>
        </template>
        <mr-bullet-list-loader
            v-else
            class="loader"
            :count="10"
            :item-height="24"
            :item-max-width="120"
            :item-min-width="60"
        />
    </SidebarBox>
</template>

<style scoped lang="scss">
.root-container {
    .pre-header {
        display: flex;
        flex-direction: column;
        padding: 16px 16px 0;
        .content {
            align-items: center;
            display: flex;
            justify-content: space-between;
            padding-bottom: 8px;
            p {
                @include tg-m($w5, usePalette($p-cool-gray, $p-500));
            }
        }
    }
    .divider {
        width: 100%;
        height: 2px;
        background-color: usePalette($p-gray, $p-100);
    }
    p {
        margin: 0;
        font-size: 12px;
        @include font-weight();
    }
}

.loader {
    margin-right: 12px;
}

:deep(.vue-slider-dot-tooltip-text) {
    font-size: 10px;
}
</style>
